import { formatDate, parseISO } from "date-fns"
import { cn } from "design-system/utils"
import { Ticket } from "lucide-react"
import { useTranslation } from "react-i18next"
import { SupportConversationTicketResponseDataSchema } from "schema"
import z from "zod"
import { TicketStatusDropdown } from "../ConversationTickets/TicketStatusDropdown"
import { TicketIntegrationBadges } from "../TicketIntegrationBadges"
import { AmphoraActivityPanel } from "./AmphoraActivityPanel"
import { GorgiasActivityPanel } from "./GorgiasActivityPanel"
import { ZendeskAuditPanel } from "./ZendeskAuditPanel"

type Props = {
  ticket: z.infer<typeof SupportConversationTicketResponseDataSchema>
}

export function TicketContent({ ticket }: Props) {
  const { t } = useTranslation()

  const hasIntegrations = ticket.integrations.length > 0

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="w-full flex flex-col sm:flex-row gap-4 justify-between">
        <div
          className={cn(
            "flex flex-row gap-1 items-center text-support dark:text-support-secondary transition-colors duration-150 group-hover/item:text-[var(--color-support)]",
            /**
             * NOTE: setting a min-h as the question has a Badge with it, so it matches both in height
             * and the timeline dots are vertically centered
             */
            !hasIntegrations && "min-h-6",
          )}
        >
          <Ticket size={16} />
          <span className="text-sm font-medium">{t("support.timeline.ticket")}</span>
        </div>
        <div className="flex flex-row gap-4 items-center">
          <div className="flex flex-row gap-2">
            <TicketStatusDropdown ticket={ticket} size="sm" className="w-auto shrink-0" />
            <TicketIntegrationBadges integrations={ticket.integrations} />
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDate(parseISO(ticket.createdAt), "Pp")}
          </span>
        </div>
      </div>
      <ZendeskAuditPanel ticketId={ticket.id} integrations={ticket.integrations} />
      <AmphoraActivityPanel ticketId={ticket.id} integrations={ticket.integrations} />
      <GorgiasActivityPanel ticketId={ticket.id} integrations={ticket.integrations} />
    </div>
  )
}
